/**
 * Kontrak data meter pemakaian AI (rancangan 06c, tahap C1). Dipakai server
 * (`/api/admin/ai-assistant/usage*`, `/model-rates`) dan kartu "Pemakaian AI".
 * Semua angka hanya untuk admin; dolar selalu ESTIMASI dari tarif admin.
 */

/** Default batas plafon — harus sama dengan default kolom di schema.prisma. 0 = tanpa batas (mode pantau). */
export const CAP_SETTINGS_DEFAULTS = {
	chatTokenLimitMonthly: 0,
	voiceMinutesLimitDaily: 0,
	voiceMinutesLimitMonthly: 0,
	chatUsdLimitDaily: 0,
	chatUsdLimitMonthly: 0,
	voiceUsdLimitDaily: 0,
	voiceUsdLimitMonthly: 0,
	capWarnPercent: 80,
} as const;

/** Rentang yang boleh diisi admin untuk batas plafon. */
export const CAP_SETTINGS_LIMITS = {
	tokensMax: 10_000_000_000,
	minutesMax: 1_000_000,
	usdMax: 100_000,
	warnPercentMin: 50,
	warnPercentMax: 99,
} as const;

export const USAGE_KINDS = ["chat", "voice"] as const;
export type UsageKind = (typeof USAGE_KINDS)[number];

export type UsagePeriod = "day" | "month";

/** `monitor` = batas 0 (tanpa batas); `ok` < ambang peringatan; `warn` ≥ ambang; `full` ≥ 100%. */
export type MeterLevel = "monitor" | "ok" | "warn" | "full";

/** Satu batang meter. Chat dalam token, Suara dalam detik (UI menampilkan menit). */
export interface UsageMeterDto {
	id: `${UsageKind}-${UsagePeriod}`;
	kind: UsageKind;
	period: UsagePeriod;
	unit: "tokens" | "seconds";
	used: number;
	/** 0 = tanpa batas (mode pantau). */
	limit: number;
	/** null bila tanpa batas. */
	percent: number | null;
	level: MeterLevel;
	/** Estimasi dolar periode ini; null bila tak ada satu pun model bertarif. */
	usd: number | null;
	/** Ada pemakaian pada model yang tarifnya belum diisi (estimasi tidak lengkap). */
	usdIncomplete: boolean;
	requests: number;
	noUsageRequests: number;
}

/** Rincian satu model pada satu periode. */
export interface UsageModelRowDto {
	kind: UsageKind;
	period: UsagePeriod;
	model: string;
	inputTokens: number;
	outputTokens: number;
	voiceSeconds: number;
	requests: number;
	noUsageRequests: number;
	/** null = tarif model belum diisi. */
	usd: number | null;
}

/** Respons `GET /api/admin/ai-assistant/usage`. */
export interface UsageOverviewDto {
	/** Tanggal WITA `YYYY-MM-DD` dan bulan `YYYY-MM`. */
	today: string;
	month: string;
	warnPercent: number;
	resetInSeconds: { day: number; month: number };
	meters: UsageMeterDto[];
	models: UsageModelRowDto[];
	/** Model berpemakaian yang tarifnya belum diisi. */
	ratesMissing: string[];
}

/** Satu hari pada grafik. */
export interface UsageDailyPointDto {
	day: string;
	chatTokens: number;
	voiceSeconds: number;
}

/** Respons `GET /api/admin/ai-assistant/usage/daily`. */
export interface UsageDailyDto {
	points: UsageDailyPointDto[];
	/** Plafon harian (0 = tanpa batas) untuk garis acuan grafik. */
	chatTokenLimitDaily: number;
	voiceSecondsLimitDaily: number;
}

export interface ModelRateDto {
	model: string;
	kind: UsageKind;
	usdPerMinute: number | null;
	inputUsdPerMTok: number | null;
	outputUsdPerMTok: number | null;
	updatedAt: string;
	/** Dipakai slot sekarang atau punya pemakaian bulan ini. */
	inUse: boolean;
}

/** Respons `GET /api/admin/ai-assistant/model-rates`. */
export interface ModelRatesDto {
	rates: ModelRateDto[];
}

/** Body `PUT /api/admin/ai-assistant/model-rates/:model`. */
export interface ModelRateUpdate {
	kind: UsageKind;
	usdPerMinute: number | null;
	inputUsdPerMTok: number | null;
	outputUsdPerMTok: number | null;
}
